
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useAdminStatus } from "@/hooks/use-admin-status";
import { useTokenOperations } from "./tokens/use-token-operations";

export function useTokens() {
  const [tokens, setTokens] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [canClaimDailyTokens, setCanClaimDailyTokens] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { isAdmin } = useAdminStatus();
  const tokenOperations = useTokenOperations();

  const fetchTokens = async () => {
    if (!isAuthenticated || !user) {
      setTokens(0);
      setIsLoading(false);
      return;
    }

    try {
      console.log("Fetching tokens for user:", user.id);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('tokens, last_daily_claim')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching tokens:', error);
        setTokens(0);
      } else if (data) {
        console.log("User data:", data);
        const userTokens = data.tokens || 0;
        setTokens(userTokens);
        console.log("Tokens set to:", userTokens);
        
        const lastClaim = data.last_daily_claim ? new Date(data.last_daily_claim) : null;
        const now = new Date();
        const eightHours = 8 * 60 * 60 * 1000;
        
        if (!lastClaim || (now.getTime() - lastClaim.getTime()) >= eightHours) {
          setCanClaimDailyTokens(true);
          console.log("Can claim daily tokens: true");
        } else {
          setCanClaimDailyTokens(false);
          console.log("Can claim daily tokens: false");
        }
      }
    } catch (error) {
      console.error('Error fetching tokens:', error);
      setTokens(0);
    } finally {
      setIsLoading(false);
    }
  };

  const claimDailyTokens = async (): Promise<boolean> => {
    const result = await tokenOperations.claimDailyTokens(tokens, canClaimDailyTokens);
    if (result.success && result.newTokens !== undefined) {
      setTokens(result.newTokens);
      setCanClaimDailyTokens(false);
    }
    return result.success;
  };

  const useToken = async (tokensToUse: number = 1): Promise<boolean> => {
    const success = await tokenOperations.useToken(tokensToUse);
    if (success && !isAdmin) {
      setTokens(prev => {
        const newBalance = prev - tokensToUse;
        console.log(`Token used successfully. New balance: ${newBalance}`);
        return newBalance;
      });
    }
    return success;
  };

  const hasEnoughTokens = (requiredTokens: number = 1): boolean => {
    if (isAdmin) {
      console.log("Admin user detected - unlimited tokens");
      return true;
    }

    const result = tokens >= requiredTokens;
    console.log(`Checking if has enough tokens: required=${requiredTokens}, available=${tokens}, result=${result}`);
    return result;
  };

  useEffect(() => {
    fetchTokens();
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const interval = setInterval(() => {
      fetchTokens();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  return {
    tokens,
    isLoading,
    canClaimDailyTokens,
    useToken,
    hasEnoughTokens,
    claimDailyTokens,
    refreshTokens: fetchTokens
  };
}
